import { useState, useEffect, useMemo } from "react";
import {
  $DepartureScheduleFilters,
  $DepartureScheduleFiltersStates,
  $DepartureScheduleStates,
  fetchDepartureSchedule,
  fetchDepartureScheduleFilters
} from "@store/departure-schedule-store";
import { $UserAddPermissions, ACTIONS, UserPriorityEnum } from "@store/user-store";
import { DepartureScheduleQueryParams, departureScheduleSortKeys } from "@interfaces/departure-schedule";
import DepartureScheduleService from "@services/departure-schedule-service";

import { TextField, Autocomplete, Stack } from "@mui/material";
import { TabSwicher } from "@ui/tab-swicher";
import { Button } from "@ui/button";
import { BodyNormal } from "@ui/fonts";
import { FileLoadSVG } from "@components/svgs";
import { DepartureScheduleParamsProvider } from "@components/tables/departure-schedule/departure-schedule-context";
import DepartureScheduleCalendar from "@components/tables/departure-schedule/departure-schedule-calendar";
import DepartureScheduleTable from "@components/tables/departure-schedule/departure-schedule";
import clsx from "clsx";
import style from "@scss/pages/company/procedure-control.module.scss";
import styleInput from "@ui/drop-down/drop-down-multi-select/drop-down-multi-select.module.scss";
import { useUnit } from "effector-react";
import { useSorting } from "@hooks/useSorting";
import { useDepartureScheduleQuery } from "@lib/daparture-schedule";
import { downloadBlob } from "@utils/downloadBlob";
import moment from "moment";

const tabs = [
  "Таблица",
  "Календарь",
];
enum Tab {
  TABLE = 0,
  CALENDAR = 1,
}

type SelectListType = {
  id: number;
  title: string;
};

const DepartureSchedule: React.FC = () => {
  const filters = useUnit($DepartureScheduleFilters);
  const [selectedTab, setSelectedTab] = useState(0);
  const [queryFilter, setQueryFilter] = useState<
    Omit<
      DepartureScheduleQueryParams,
      "address"
    >
  >({});
  const [listIds, setListIds] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [selectedCompanies, setSelectedCompanies] = useState<SelectListType[]>([]);
  const [selectedContact, setSelectedContact] = useState<SelectListType[]>([]);
  const [selectedCountDeparture, setSelectedCountDeparture] = useState<SelectListType[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<SelectListType[]>([]);
  const [selectedStatusesMonth, setSelectedStatusesMonth] = useState<SelectListType[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectListType[]>([]);
  const sorting = useSorting(departureScheduleSortKeys, "ASC");
  const permissions = useUnit($UserAddPermissions);

  const { isFetched } = useUnit($DepartureScheduleStates);
  const { isFetched: isFetchedFilter } = useUnit($DepartureScheduleFiltersStates);

  const departureScheduleParams = {
    ...queryFilter,
    company: selectedCompanies.map(e => e.id),
    users: selectedUsers.map(e => e.id),
    contact: selectedContact.map(e => e.id),
    count_departure: selectedCountDeparture.map(e => e.title),
    statuses: selectedStatuses.map(e => e.title),
    statuses_month: selectedStatusesMonth.map(e => e.id),
    address: search,
    ...sorting.getActiveSorting(),
  };

  useEffect(() => {
    if(!isFetched) fetchDepartureSchedule({});
    if(!isFetchedFilter) fetchDepartureScheduleFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlerChangeFilter = (key: string, value: any) => {
    if (value === "") {
      return setQueryFilter((prevFilters) => ({
        ...prevFilters,
        [key]: undefined,
      }));
    }

    setQueryFilter((prevFilters) => ({ ...prevFilters, [key]: value }));
    switch (key) {
      case "company":
        return setSelectedCompanies(value);
      case "contact":
        return setSelectedContact(value);
      case "count_departure":
        return setSelectedCountDeparture(value);
      case "statuses":
        return setSelectedStatuses(value);
      case "statuses_month":
        return setSelectedStatusesMonth(value);
      case "users":
        return setSelectedUsers(value);
      case "address":
        return setSearch(value);
    }
  };

  const companiesList = useMemo(() => {
    return [
      ...(filters.company?.map((item) => ({
        title: item.name,
        id: item.id,
      })) ?? []),
    ] as SelectListType[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const contactsList = useMemo(() => {
    return [
      ...(filters.contact?.map((item) => ({
        title: item.name,
        id: item.id!,
      })) ?? []),
    ] as SelectListType[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const countDepartureList = useMemo(() => {
    return [
      ...(filters.count_departure?.map((item,i) => ({
        title: item.title,
        id: i,
      })) ?? []),
    ] as SelectListType[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const statusesList = useMemo(() => {
    return [
      ...(filters.statuses?.map((item,i) => ({
        title: item,
        id: i,
      })) ?? []),
    ] as SelectListType[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const monthList = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = moment().startOf("year").add(i, "M");
      return {
        title: month.format("MMMM"),
        id: i
      }
    })
  }, []);
  const usersList = useMemo(() => {
    return [
      ...(filters.users?.map((item,i) => ({
        title: item.name,
        id: item.id,
      })) ?? []),
    ] as SelectListType[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const isOwner = permissions.hasRequiredLevel(UserPriorityEnum.Owner) || permissions.hasRequiredLevel(UserPriorityEnum.SuperAdmin);
  const dropDownData = useMemo(() => {
    const data = [
      {
        label: "Компания",
        list: companiesList,
        type: "company",
        value: selectedCompanies,
      },
      {
        label: "Контактное лицо",
        list: contactsList,
        type: "contact",
        value: selectedContact,
      },
      {
        label: "Кол-во выездов",
        list: countDepartureList,
        type: "count_departure",
        value: selectedCountDeparture,
      },
    ];

    if(isOwner){
      data.unshift({
        label: "Сотрудник",
        list: usersList,
        type: "users",
        value: selectedUsers,
      })
    }

    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companiesList,
    contactsList,
    countDepartureList,
    usersList,
    selectedCompanies,
    selectedContact,
    selectedCountDeparture,
    selectedUsers,
    isOwner,
    filters,
  ]);

  const dropDownMonthData = useMemo(() => {
    return [
      {
        label: "Месяц выезда",
        list: monthList,
        type: "statuses_month",
        value: selectedStatusesMonth,
      },
      {
        label: "Статус выезда",
        list: statusesList,
        type: "statuses",
        value: selectedStatuses,
      }
    ]
  }, [
    statusesList,
    monthList,
    selectedStatuses,
    selectedStatusesMonth,
  ])

  const handleFilterAndSearch = () => {
    handlerChange(listIds, queryFilter, search);
  };

  const handlerChange = (
    listIds: string,
    filter?: DepartureScheduleQueryParams,
    search?: string
  ) => {
    setListIds(listIds);
    setQueryFilter(
      Object.assign({}, {
        ...filter,
        searchString: search,
      })
    );
  };

  const departureScheduleQuery = useDepartureScheduleQuery(departureScheduleParams);

  const handleDownloadExcelFile = () => {
    DepartureScheduleService.getExcel(departureScheduleParams, (err, res) => {
      if (err || !res) return;

      downloadBlob(res.data, `Таблица выездов от ${moment().format('DD.MM.YYYY HH:mm:ss')}.xlsx`);
    })
  };

  return (
    <div className={clsx(style.content)}>
      <Stack className={style.head} sx={{ position: "relative", mb: 1 }} gap={1} alignItems="center" direction="row">
        <TabSwicher
          onChange={setSelectedTab}
          value={selectedTab}
          labels={tabs}
        />
        <div className={style.direction_list}>
          <div
            className={style.direction_list_dowload}
            tabIndex={0}
            onClick={handleDownloadExcelFile}
          >
            <FileLoadSVG />
            Скачать текущую таблицу
          </div>
        </div>
      </Stack>
      <div className={style.modules_table}>
        <DepartureScheduleParamsProvider value={departureScheduleParams}>
          <Stack className={style.head_container}>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'nowrap', width: '100%' }}>
              {dropDownData.map(({ label, list, type, value }) => (
                <Autocomplete
                  multiple
                  options={list}
                  getOptionLabel={(option) => option.title}
                  getOptionKey={(option) => option.id}
                  value={value}
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(e, value) => handlerChangeFilter(type, value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      size="small"
                      label={label}
                      placeholder={`Введите ${label.toLowerCase()}`}
                      className={clsx(
                        styleInput.box_input_n,
                        styleInput.box_input_n_smile,
                        styleInput.box_input_height,
                      )}
                    />
                  )}
                  sx={{ flex: 1 }}
                  slotProps={{
                    listbox: {
                      sx: {
                        padding: "0",
                      },
                    },
                    popper: {
                      popperOptions: {
                        placement: "bottom-start",
                      },
                      sx: {
                        width: "auto !important",
                        maxWidth: "30vw",
                      },
                    },
                  }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'nowrap', width: '100%' }}>
              {dropDownMonthData.map(({ label, list, type, value }) => (
                <Autocomplete
                  multiple
                  options={list}
                  getOptionLabel={(option) => option.title}
                  getOptionKey={(option) => option.id}
                  value={value}
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(e, value) => handlerChangeFilter(type, value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      size="small"
                      label={label}
                      placeholder={`Введите ${label.toLowerCase()}`}
                      className={clsx(
                        styleInput.box_input_n,
                        styleInput.box_input_n_smile,
                        styleInput.box_input_height,
                      )}
                    />
                  )}
                  sx={{ flex: 1, maxWidth: '15%' }}
                />
              ))}
              <Autocomplete
                freeSolo
                options={filters.address}
                getOptionLabel={(option) => option}
                getOptionKey={(option) => option}
                value={search}
                isOptionEqualToValue={(option, value) => option === value}
                onChange={(e, value) => handlerChangeFilter("address", value)}
                onInputChange={(e, value) => handlerChangeFilter("address", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    size="small"
                    label="Адрес"
                    placeholder="Введите адрес"
                    className={clsx(
                      styleInput.box_input_n,
                      styleInput.box_input_n_smile,
                    )}
                  />
                )}
                sx={{ flex: 1 }}
                slotProps={{
                  listbox: {
                    sx: {
                      padding: "0",
                    },
                  },
                  popper: {
                    popperOptions: {
                      placement: "bottom-start",
                    },
                    sx: {
                      width: "auto !important",
                      maxWidth: "30vw",
                    },
                  },
                }}
              />
              <Button
                className={style.btnFilter}
                onClick={handleFilterAndSearch}
              >
                <BodyNormal>Фильтровать</BodyNormal>
              </Button>
            </Stack>
          </Stack>
          {selectedTab === Tab.TABLE && (
            <DepartureScheduleTable
              sorting={sorting}
              isLoading={departureScheduleQuery.isLoading}
              query={departureScheduleQuery}
            />
          )}
          {selectedTab === Tab.CALENDAR && (
            <DepartureScheduleCalendar
              isLoading={departureScheduleQuery.isLoading}
              query={departureScheduleQuery}
            />
          )}
        </DepartureScheduleParamsProvider>
      </div>
    </div>
  );
};

export default DepartureSchedule;
